<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { DOCUMENT_TYPE, type DocumentType } from '@hsl-ems/contracts';

import { p1Api } from '@/lib/p1-api';
import { p3Api, type ApprovalTaskSummary } from '@/lib/p3-api';

const router = useRouter();
const view = ref<'PENDING' | 'DONE'>('PENDING');
const tasks = ref<ApprovalTaskSummary[]>([]);
const loading = ref(true);
const loadingMore = ref(false);
const error = ref('');
const nextCursor = ref<string | null>(null);
const hasNextPage = ref(false);
const canTransfer = ref(false);
const transferTasks = ref<ApprovalTaskSummary[]>([]);

const typeLabels: Record<DocumentType, string> = {
  [DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT]: '费用报销',
  [DOCUMENT_TYPE.LOAN]: '借款申请',
  [DOCUMENT_TYPE.CASH_REPAYMENT]: '现金还款',
  [DOCUMENT_TYPE.LOAN_OFFSET]: '借款冲销',
  [DOCUMENT_TYPE.CORPORATE_PAYMENT]: '对公付款',
};

onMounted(async () => {
  try {
    const me = await p1Api.me();
    if (me.mustChangePassword) return void (await router.replace('/change-password'));
    if (!me.permissions.includes('approval:decide')) {
      if (!me.permissions.includes('approval:transfer')) {
        error.value = '当前账号没有审批待办权限';
        loading.value = false;
        return;
      }
    }
    canTransfer.value =
      me.permissions.includes('approval:transfer') && me.roleCodes.includes('GROUP_ADMIN');
    await Promise.all([
      me.permissions.includes('approval:decide') ? load() : Promise.resolve(),
      canTransfer.value ? loadTransferTasks() : Promise.resolve(),
    ]);
    if (!me.permissions.includes('approval:decide')) loading.value = false;
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : '待办加载失败';
    if (message.includes('登录已失效')) return void (await router.replace('/login'));
    error.value = message;
    loading.value = false;
  }
});

async function loadTransferTasks() {
  try {
    transferTasks.value = await p3Api.transferableTasks();
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '范围内待办加载失败';
  }
}

async function load(append = false) {
  if (append) loadingMore.value = true;
  else loading.value = true;
  error.value = '';
  try {
    const result = await p3Api.tasks(
      view.value,
      append ? (nextCursor.value ?? undefined) : undefined,
    );
    tasks.value = append ? [...tasks.value, ...result.items] : result.items;
    nextCursor.value = result.pageInfo.nextCursor;
    hasNextPage.value = result.pageInfo.hasNextPage;
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '待办加载失败';
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
}

async function changeView(next: 'PENDING' | 'DONE') {
  if (view.value === next) return;
  view.value = next;
  nextCursor.value = null;
  await load();
}

function money(value: string) {
  return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(
    Number(value),
  );
}

function dateTime(value: string | null) {
  if (!value) return '未设置';
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
</script>

<template>
  <main class="workspace-shell approval-shell">
    <header class="workspace-header">
      <RouterLink class="workspace-brand" to="/documents">
        <span class="brand-mark" aria-hidden="true">郎</span>
        <span><strong>郎报通</strong><small>审批工作台</small></span>
      </RouterLink>
      <nav aria-label="主导航">
        <RouterLink to="/documents">我的单据</RouterLink>
        <RouterLink class="active" to="/approval-tasks">审批待办</RouterLink>
        <RouterLink to="/admin">组织与权限</RouterLink>
      </nav>
      <RouterLink class="text-link" to="/login">切换账号</RouterLink>
    </header>

    <section class="page-heading">
      <div>
        <p class="eyebrow">P3 · 审批处理</p>
        <h1>审批工作台</h1>
        <p>只显示分配给当前账号的任务，组织权限不会自动扩大待办范围。</p>
      </div>
      <button class="quiet-button" type="button" :disabled="loading" @click="load()">刷新</button>
    </section>

    <div class="approval-tabs" role="tablist" aria-label="审批任务视图">
      <button
        type="button"
        role="tab"
        :aria-selected="view === 'PENDING'"
        :class="{ active: view === 'PENDING' }"
        @click="changeView('PENDING')"
      >
        我的待办
      </button>
      <button
        type="button"
        role="tab"
        :aria-selected="view === 'DONE'"
        :class="{ active: view === 'DONE' }"
        @click="changeView('DONE')"
      >
        已办与关闭
      </button>
    </div>

    <p v-if="error" class="inline-alert inline-alert--error" role="alert">{{ error }}</p>
    <section v-if="loading" class="document-empty">正在加载审批任务…</section>
    <section v-else-if="!tasks.length" class="document-empty">
      <strong>{{ view === 'PENDING' ? '当前没有待处理任务' : '当前没有已办记录' }}</strong>
      <p>
        {{ view === 'PENDING' ? '新任务生成后会显示在这里。' : '处理或关闭的任务会保留历史。' }}
      </p>
    </section>
    <section v-else class="approval-list" aria-label="审批任务列表">
      <RouterLink
        v-for="task in tasks"
        :key="task.id"
        class="approval-row"
        :to="`/approval-tasks/${task.id}`"
      >
        <span class="document-icon">{{ task.node.sequence }}</span>
        <span class="document-main">
          <span class="document-title">
            <strong>{{ task.instance.document.documentNo }}</strong>
            <span class="status-pill" :data-status="task.status">{{ task.status }}</span>
          </span>
          <span>
            {{ task.node.name }} · {{ typeLabels[task.instance.document.type] }} ·
            {{ task.instance.document.applicant.name }}
          </span>
          <small>
            {{ task.instance.document.description }} ·
            {{
              task.dueAt
                ? `期望 ${dateTime(task.dueAt)} 前处理`
                : `创建于 ${dateTime(task.createdAt)}`
            }}
          </small>
        </span>
        <strong class="document-amount">{{ money(task.instance.document.requestedAmount) }}</strong>
        <span aria-hidden="true">›</span>
      </RouterLink>
    </section>

    <div v-if="hasNextPage" class="load-more">
      <button class="quiet-button" type="button" :disabled="loadingMore" @click="load(true)">
        {{ loadingMore ? '正在加载…' : '加载更多' }}
      </button>
    </div>

    <section v-if="canTransfer" class="form-section transfer-scope-panel">
      <div class="section-heading">
        <div>
          <p class="eyebrow">集团管理员</p>
          <h2>范围内待转交任务</h2>
          <p>仅列出授权组织内仍在运行的待办；进入详情后选择新处理人并填写原因。</p>
        </div>
        <button class="quiet-button" type="button" @click="loadTransferTasks">刷新</button>
      </div>
      <div v-if="transferTasks.length" class="approval-list compact-approval-list">
        <RouterLink
          v-for="task in transferTasks"
          :key="task.id"
          class="approval-row"
          :to="`/approval-tasks/${task.id}`"
        >
          <span class="document-icon">{{ task.node.sequence }}</span>
          <span class="document-main">
            <strong>{{ task.instance.document.documentNo }} · {{ task.node.name }}</strong>
            <small
              >{{ task.instance.document.applicant.name }} ·
              {{ task.instance.document.description }}</small
            >
          </span>
          <strong class="document-amount">{{
            money(task.instance.document.requestedAmount)
          }}</strong>
          <span>转交 ›</span>
        </RouterLink>
      </div>
      <div v-else class="compact-empty">授权范围内没有可转交待办</div>
    </section>

    <nav class="mobile-nav" aria-label="移动端主导航">
      <RouterLink to="/documents">单据</RouterLink>
      <RouterLink class="active" to="/approval-tasks">审批</RouterLink>
      <RouterLink to="/admin">我的</RouterLink>
    </nav>
  </main>
</template>
